import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, IconButton, TextField, MenuItem, Select, FormControl,
    InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    Alert, Tooltip, Chip, InputAdornment, Switch, FormControlLabel, Collapse
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Edit as EditIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
    WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { pricingAPI, carTypesAPI } from '../../services/apiService';

const RoutePricesManager = () => {
    const [routes, setRoutes] = useState([]);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [carTypes, setCarTypes] = useState([]);

    const carTypeLabel = (val) => carTypes.find(c => c.id === val)?.name || `Type ${val}`;

    // Grouped routes: { "ORIGIN→DEST": [routePrice, ...] }
    const [grouped, setGrouped] = useState({});
    const [expandedGroup, setExpandedGroup] = useState(null);

    // Inline editing
    const [editingId, setEditingId] = useState(null);
    const [editPrice, setEditPrice] = useState('');
    const [editOwnerCut, setEditOwnerCut] = useState('');
    const [editBothDirections, setEditBothDirections] = useState(true);

    // Inline add for missing car types: { key, carTypeId }
    const [addingFor, setAddingFor] = useState(null);
    const [addPrice, setAddPrice] = useState('');
    const [addOwnerCut, setAddOwnerCut] = useState('');
    const [addSaving, setAddSaving] = useState(false);

    // Create form
    const [newOriginId, setNewOriginId] = useState('');
    const [newDestId, setNewDestId] = useState('');
    const [bothDirections, setBothDirections] = useState(true);
    const [newPrices, setNewPrices] = useState([]);
    const [creating, setCreating] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [routeData, zoneData, carTypeData] = await Promise.all([
                pricingAPI.getRoutes(),
                pricingAPI.getZones(),
                carTypesAPI.getAll(),
            ]);
            setRoutes(routeData);
            setZones(zoneData);
            const sorted = (carTypeData || []).sort((a, b) => a.sortOrder - b.sortOrder);
            setCarTypes(sorted);
            setNewPrices(sorted.map(ct => ({ carType: ct.id, price: '', ownerCut: '' })));

            // Group by origin→dest
            const groups = {};
            for (const rp of routeData) {
                const origin = rp.originZone?.name || `Zone ${rp.originZoneId}`;
                const dest = rp.destinationZone?.name || `Zone ${rp.destinationZoneId}`;
                const key = `${origin}→${dest}`;
                if (!groups[key]) groups[key] = { originZoneId: rp.originZoneId, destinationZoneId: rp.destinationZoneId, routes: [] };
                groups[key].routes.push(rp);
            }
            setGrouped(groups);
            setError(null);
        } catch (err) {
            setError('Failed to load data: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleInlineEdit = (rp) => {
        setEditingId(rp.id);
        setEditPrice(rp.price.toString());
        setEditOwnerCut(rp.ownerCut.toString());
    };

    const handleInlineSave = async (id) => {
        try {
            if (editBothDirections) {
                await pricingAPI.updateRoutePriceBoth(id, parseFloat(editPrice), parseFloat(editOwnerCut));
            } else {
                await pricingAPI.updateRoutePrice(id, parseFloat(editPrice), parseFloat(editOwnerCut));
            }
            setEditingId(null);
            setSuccess('Price updated');
            setTimeout(() => setSuccess(null), 2000);
            await fetchData();
        } catch (err) {
            setError('Update failed: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleAddInline = async (group) => {
        try {
            setAddSaving(true);
            await pricingAPI.addSingleRoutePrice(
                group.originZoneId,
                group.destinationZoneId,
                addingFor.carTypeId,
                parseFloat(addPrice),
                parseFloat(addOwnerCut) || 0,
            );
            setAddingFor(null);
            setAddPrice('');
            setAddOwnerCut('');
            setSuccess('Price added');
            setTimeout(() => setSuccess(null), 2000);
            await fetchData();
        } catch (err) {
            setError('Add failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setAddSaving(false);
        }
    };

    const handleCreate = async () => {
        try {
            setCreating(true);
            const prices = newPrices
                .filter(p => p.price && parseFloat(p.price) > 0)
                .map(p => ({
                    carType: p.carType,
                    price: parseFloat(p.price),
                    ownerCut: parseFloat(p.ownerCut) || 0,
                }));

            if (prices.length === 0) {
                setError('Enter at least one car type price');
                setCreating(false);
                return;
            }

            await pricingAPI.createRoutes({
                originZoneId: parseInt(newOriginId),
                destinationZoneId: parseInt(newDestId),
                createBothDirections: bothDirections,
                prices,
            });

            setCreateOpen(false);
            setNewOriginId('');
            setNewDestId('');
            setBothDirections(true);
            setNewPrices(carTypes.map(ct => ({ carType: ct.id, price: '', ownerCut: '' })));
            setSuccess('Route prices created');
            setTimeout(() => setSuccess(null), 3000);
            await fetchData();
        } catch (err) {
            setError('Create failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setCreating(false);
        }
    };

    const handleDeletePair = async (originZoneId, destinationZoneId) => {
        try {
            await pricingAPI.deleteRoutes(originZoneId, destinationZoneId);
            setDeleteConfirm(null);
            setSuccess('Route pair deleted');
            setTimeout(() => setSuccess(null), 2000);
            await fetchData();
        } catch (err) {
            setError('Delete failed: ' + (err.response?.data?.error || err.message));
            setDeleteConfirm(null);
        }
    };

    // Filter zones that should not be used as route endpoints (normalized zones)
    const routeZones = zones.filter(z => !z.normalizeTo);

    if (loading) {
        return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
    }

    const groupKeys = Object.keys(grouped).sort();

    return (
        <Box>
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Route Prices ({groupKeys.length} routes, {routes.length} prices)</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>Add Route</Button>
            </Box>

            {/* Route groups as expandable rows */}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={40}></TableCell>
                            <TableCell><strong>Route</strong></TableCell>
                            <TableCell align="center"><strong>Car Types</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groupKeys.map((key) => {
                            const group = grouped[key];
                            const isExpanded = expandedGroup === key;
                            const existingCarTypeIds = new Set(group.routes.map(rp => rp.carType));
                            const missingCarTypes = carTypes.filter(ct => !existingCarTypeIds.has(ct.id));
                            return (
                                <React.Fragment key={key}>
                                    <TableRow
                                        hover
                                        sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                                        onClick={() => setExpandedGroup(isExpanded ? null : key)}
                                    >
                                        <TableCell>
                                            {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <strong>{key}</strong>
                                                {missingCarTypes.length > 0 && (
                                                    <Tooltip title={`Missing prices for: ${missingCarTypes.map(ct => ct.name).join(', ')}`}>
                                                        <WarningIcon fontSize="small" sx={{ color: 'error.main' }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
                                                {group.routes.sort((a, b) => carTypes.findIndex(ct => ct.id === a.carType) - carTypes.findIndex(ct => ct.id === b.carType)).map(rp => (
                                                    <Chip
                                                        key={rp.id}
                                                        label={`${carTypeLabel(rp.carType)}: $${rp.price}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                                {missingCarTypes.map(ct => (
                                                    <Tooltip key={ct.id} title={`No price set for ${ct.name} on this route`}>
                                                        <Chip
                                                            label={`${ct.name}: --`}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ borderColor: 'error.main', color: 'error.main' }}
                                                        />
                                                    </Tooltip>
                                                ))}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Delete entire route pair (both directions)">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(group); }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>

                                    {/* Expanded detail rows */}
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ p: 0 }}>
                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell><strong>Car Type</strong></TableCell>
                                                                <TableCell align="right"><strong>Price</strong></TableCell>
                                                                <TableCell align="right"><strong>Owner Cut</strong></TableCell>
                                                                <TableCell align="right"><strong>Driver Gets</strong></TableCell>
                                                                <TableCell align="right"><strong>Actions</strong></TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {carTypes.map(ct => {
                                                                const rp = group.routes.find(r => r.carType === ct.id);
                                                                const isAdding = addingFor?.key === key && addingFor?.carTypeId === ct.id;

                                                                if (rp) {
                                                                    // Existing price row
                                                                    return (
                                                                        <TableRow key={rp.id}>
                                                                            <TableCell>{ct.name}</TableCell>
                                                                            {editingId === rp.id ? (
                                                                                <>
                                                                                    <TableCell align="right">
                                                                                        <TextField
                                                                                            size="small"
                                                                                            value={editPrice}
                                                                                            onChange={(e) => setEditPrice(e.target.value)}
                                                                                            type="number"
                                                                                            sx={{ width: 100 }}
                                                                                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                                                        />
                                                                                    </TableCell>
                                                                                    <TableCell align="right">
                                                                                        <TextField
                                                                                            size="small"
                                                                                            value={editOwnerCut}
                                                                                            onChange={(e) => setEditOwnerCut(e.target.value)}
                                                                                            type="number"
                                                                                            sx={{ width: 100 }}
                                                                                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                                                        />
                                                                                    </TableCell>
                                                                                    <TableCell align="right">
                                                                                        ${(parseFloat(editPrice || 0) - parseFloat(editOwnerCut || 0)).toFixed(2)}
                                                                                    </TableCell>
                                                                                    <TableCell align="right">
                                                                                        <IconButton size="small" color="primary" onClick={() => handleInlineSave(rp.id)}>
                                                                                            <SaveIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                        <IconButton size="small" onClick={() => setEditingId(null)}>
                                                                                            <CancelIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                        <FormControlLabel
                                                                                            control={<Switch size="small" checked={editBothDirections} onChange={(e) => setEditBothDirections(e.target.checked)} />}
                                                                                            label="Both"
                                                                                            sx={{ ml: 1, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                                                                        />
                                                                                    </TableCell>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <TableCell align="right">${rp.price.toFixed(2)}</TableCell>
                                                                                    <TableCell align="right">${rp.ownerCut.toFixed(2)}</TableCell>
                                                                                    <TableCell align="right">${(rp.price - rp.ownerCut).toFixed(2)}</TableCell>
                                                                                    <TableCell align="right">
                                                                                        <Tooltip title="Edit price">
                                                                                            <IconButton size="small" onClick={() => handleInlineEdit(rp)}>
                                                                                                <EditIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    </TableCell>
                                                                                </>
                                                                            )}
                                                                        </TableRow>
                                                                    );
                                                                } else if (isAdding) {
                                                                    // Inline add form
                                                                    return (
                                                                        <TableRow key={`add-${ct.id}`} sx={{ bgcolor: 'rgba(25, 118, 210, 0.06)' }}>
                                                                            <TableCell sx={{ fontWeight: 'bold' }}>{ct.name}</TableCell>
                                                                            <TableCell align="right">
                                                                                <TextField
                                                                                    size="small" autoFocus
                                                                                    value={addPrice}
                                                                                    onChange={(e) => setAddPrice(e.target.value)}
                                                                                    type="number"
                                                                                    sx={{ width: 100 }}
                                                                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell align="right">
                                                                                <TextField
                                                                                    size="small"
                                                                                    value={addOwnerCut}
                                                                                    onChange={(e) => setAddOwnerCut(e.target.value)}
                                                                                    type="number"
                                                                                    sx={{ width: 100 }}
                                                                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell align="right">
                                                                                {addPrice ? `$${(parseFloat(addPrice || 0) - parseFloat(addOwnerCut || 0)).toFixed(2)}` : '—'}
                                                                            </TableCell>
                                                                            <TableCell align="right">
                                                                                <IconButton
                                                                                    size="small" color="primary"
                                                                                    disabled={addSaving || !addPrice || parseFloat(addPrice) <= 0}
                                                                                    onClick={() => handleAddInline(group)}
                                                                                >
                                                                                    <SaveIcon fontSize="small" />
                                                                                </IconButton>
                                                                                <IconButton size="small" onClick={() => setAddingFor(null)}>
                                                                                    <CancelIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                } else {
                                                                    // Missing — show add button
                                                                    return (
                                                                        <TableRow key={`missing-${ct.id}`} sx={{ bgcolor: 'rgba(211, 47, 47, 0.06)' }}>
                                                                            <TableCell sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                                                    <WarningIcon fontSize="small" />
                                                                                    {ct.name}
                                                                                </Box>
                                                                            </TableCell>
                                                                            <TableCell align="right" sx={{ color: 'text.disabled' }}>—</TableCell>
                                                                            <TableCell align="right" sx={{ color: 'text.disabled' }}>—</TableCell>
                                                                            <TableCell align="right" sx={{ color: 'text.disabled' }}>—</TableCell>
                                                                            <TableCell align="right">
                                                                                <Tooltip title={`Add price for ${ct.name}`}>
                                                                                    <IconButton
                                                                                        size="small" color="primary"
                                                                                        onClick={() => { setAddingFor({ key, carTypeId: ct.id }); setAddPrice(''); setAddOwnerCut(''); setEditingId(null); }}
                                                                                    >
                                                                                        <AddIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                }
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create Route Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Route Prices</DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" flexDirection="column" gap={2} pt={1}>
                        <Box display="flex" gap={2}>
                            <FormControl fullWidth>
                                <InputLabel>Origin Zone</InputLabel>
                                <Select value={newOriginId} onChange={(e) => setNewOriginId(e.target.value)} label="Origin Zone">
                                    {routeZones.map(z => (
                                        <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Destination Zone</InputLabel>
                                <Select value={newDestId} onChange={(e) => setNewDestId(e.target.value)} label="Destination Zone">
                                    {routeZones.map(z => (
                                        <MenuItem key={z.id} value={z.id} disabled={z.id === newOriginId}>{z.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <FormControlLabel
                            control={<Switch checked={bothDirections} onChange={(e) => setBothDirections(e.target.checked)} />}
                            label="Create both directions (A→B and B→A with same prices)"
                        />

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>Prices per Car Type</Typography>
                        <Typography variant="caption" color="text.secondary">Leave price blank to skip a car type</Typography>

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Car Type</strong></TableCell>
                                    <TableCell><strong>Price ($)</strong></TableCell>
                                    <TableCell><strong>Owner Cut ($)</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {newPrices.map((p, idx) => (
                                    <TableRow key={p.carType}>
                                        <TableCell>{carTypeLabel(p.carType)}</TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={p.price}
                                                onChange={(e) => {
                                                    const upd = [...newPrices];
                                                    upd[idx] = { ...upd[idx], price: e.target.value };
                                                    setNewPrices(upd);
                                                }}
                                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                sx={{ width: 120 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={p.ownerCut}
                                                onChange={(e) => {
                                                    const upd = [...newPrices];
                                                    upd[idx] = { ...upd[idx], ownerCut: e.target.value };
                                                    setNewPrices(upd);
                                                }}
                                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                                sx={{ width: 120 }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
                    <Button
                        onClick={handleCreate}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={creating || !newOriginId || !newDestId}
                    >
                        {creating ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>Delete Route Pair?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will delete <strong>all car type prices</strong> for this route pair (both directions).
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button
                        onClick={() => handleDeletePair(deleteConfirm.originZoneId, deleteConfirm.destinationZoneId)}
                        color="error"
                        variant="contained"
                    >
                        Delete All
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RoutePricesManager;
